import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { geoMercator, geoPath, geoCentroid } from "d3-geo";
import { X, MapPin, Plus, Minus } from "lucide-react";
import { api, useApp } from "@/context/AppContext";

const W = 900, H = 620;
const MIN_W = W / 8;              // zoom máximo
let GEO_CACHE = null;             // geojson cacheado entre montajes
let DATA_CACHE = null;            // destinos por estado cacheados

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export const normState = (s = "") => {
  const x = s.trim().toLowerCase();
  if (x === "méxico" || x === "mexico" || x.includes("estado de m") || x.startsWith("edo")) return "Estado de México";
  if (x.includes("ciudad de m") || x === "cdmx" || x.includes("distrito federal")) return "Ciudad de México";
  return s.trim();
};

export const MEGALOPOLIS = ["Ciudad de México", "Estado de México", "Morelos", "Tlaxcala", "Puebla"];

// Separa marcadores encimados (centro de México) sin perder su estado de origen.
const spread = (pts, minDist) => {
  const out = pts.map((p) => ({ ...p, cx0: p.cx, cy0: p.cy }));
  for (let iter = 0; iter < 80; iter++) {
    let moved = false;
    for (let i = 0; i < out.length; i++)
      for (let j = i + 1; j < out.length; j++) {
        const dx = out[j].cx - out[i].cx, dy = out[j].cy - out[i].cy;
        const dist = Math.hypot(dx, dy) || 0.01;
        if (dist < minDist) {
          const push = (minDist - dist) / 2, ux = dx / dist, uy = dy / dist;
          out[i].cx -= ux * push; out[i].cy -= uy * push;
          out[j].cx += ux * push; out[j].cy += uy * push;
          moved = true;
        }
      }
    if (!moved) break;
  }
  return out;
};

export const MexicoMap = ({ focusStates = null, showFilter = false, testid = "mexico-map" }) => {
  const { t, lang } = useApp();
  const [geo, setGeo] = useState(GEO_CACHE);
  const [data, setData] = useState(DATA_CACHE || {});
  const [selected, setSelected] = useState(null);
  const [pillFocus, setPillFocus] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [vb, setVbState] = useState({ x: 0, y: 0, w: W, h: H });
  const popRef = useRef(null);
  const svgRef = useRef(null);
  const vbRef = useRef({ x: 0, y: 0, w: W, h: H });
  const pointers = useRef(new Map());
  const drag = useRef({ active: false, lastX: 0, lastY: 0, panned: false, pinchDist: 0 });

  const setVb = (updater) => setVbState((prev) => {
    const nv = typeof updater === "function" ? updater(prev) : updater;
    vbRef.current = nv;
    return nv;
  });

  useEffect(() => {
    if (!GEO_CACHE) {
      fetch("/mexico-estados.json").then((r) => r.json())
        .then((g) => { GEO_CACHE = g; setGeo(g); }).catch((e) => console.error("geojson", e));
    }
    if (!DATA_CACHE) {
      api.get("/destinations/map").then((r) => {
        const m = {}; (r.data.states || []).forEach((s) => { m[normState(s.state)] = s; });
        DATA_CACHE = m; setData(m);
      }).catch((e) => console.error("map data", e));
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const effectiveFocus = focusStates || pillFocus;
  const focusKey = (effectiveFocus || []).join(",");

  const focusSet = useMemo(
    () => (effectiveFocus ? new Set(effectiveFocus.map(normState)) : null),
    [focusKey]
  );

  const diffLabel = (d) => (t?.difficulty?.[String(d || "").toLowerCase()] || d);

  const { shapes, markers, ready } = useMemo(() => {
    if (!geo) return { shapes: [], markers: [], ready: false };
    const fit = focusSet
      ? { type: "FeatureCollection", features: geo.features.filter((f) => focusSet.has(normState(f.properties.name))) }
      : geo;
    const projection = geoMercator().fitExtent([[24, 24], [W - 24, H - 24]], fit);
    const pg = geoPath(projection);
    const sh = geo.features.map((f) => {
      const name = normState(f.properties.name);
      const active = (!focusSet || focusSet.has(name)) && (data[name]?.count || 0) > 0;
      const c = projection(geoCentroid(f));
      return { id: f.properties.id, name, d: pg(f), active, cx: c?.[0], cy: c?.[1], info: data[name] };
    });
    const mk = spread(sh.filter((s) => s.active && s.cx != null), 36)
      .sort((a, b) => a.info.count - b.info.count);
    return { shapes: sh, markers: mk, ready: true };
  }, [geo, data, focusSet]);

  // Reinicia el encuadre al cambiar el filtro.
  useEffect(() => { setVb({ x: 0, y: 0, w: W, h: H }); }, [focusKey]);

  // Cierra el popup con Escape o clic fuera del mapa.
  useEffect(() => {
    if (!selected) return;
    const onDown = (e) => {
      if (svgRef.current && svgRef.current.contains(e.target)) return; // pan dentro del mapa no cierra
      if (popRef.current && !popRef.current.contains(e.target)) setSelected(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setSelected(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [selected]);

  const toSvg = (clientX, clientY) => {
    const r = svgRef.current.getBoundingClientRect();
    const v = vbRef.current;
    return { x: v.x + ((clientX - r.left) / r.width) * v.w, y: v.y + ((clientY - r.top) / r.height) * v.h };
  };

  const zoomAt = (factor, sx, sy) => setVb((v) => {
    const nw = clamp(v.w * factor, MIN_W, W);
    const nh = nw * (H / W);
    const rx = (sx - v.x) / v.w, ry = (sy - v.y) / v.h;
    let nx = sx - rx * nw, ny = sy - ry * nh;
    nx = clamp(nx, -0.15 * W, W - nw + 0.15 * W);
    ny = clamp(ny, -0.15 * H, H - nh + 0.15 * H);
    return { x: nx, y: ny, w: nw, h: nh };
  });

  const zoomCenter = (factor) => { const v = vbRef.current; zoomAt(factor, v.x + v.w / 2, v.y + v.h / 2); };

  // Zoom con rueda (listener nativo no pasivo).
  useEffect(() => {
    const el = svgRef.current;
    if (!el || !ready) return;
    const onWheel = (e) => { e.preventDefault(); const p = toSvg(e.clientX, e.clientY); zoomAt(e.deltaY < 0 ? 0.85 : 1.18, p.x, p.y); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ready]);

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) { drag.current = { active: true, lastX: e.clientX, lastY: e.clientY, panned: false, pinchDist: 0 }; }
    else if (pointers.current.size === 2) { const [a, b] = [...pointers.current.values()]; drag.current.pinchDist = Math.hypot(a.x - b.x, a.y - b.y); drag.current.active = false; }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (drag.current.pinchDist) { const mid = toSvg((a.x + b.x) / 2, (a.y + b.y) / 2); zoomAt(drag.current.pinchDist / dist, mid.x, mid.y); }
      drag.current.pinchDist = dist;
      return;
    }
    if (drag.current.active) {
      const dx = e.clientX - drag.current.lastX, dy = e.clientY - drag.current.lastY;
      if (!drag.current.panned && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        drag.current.panned = true;
        // Captura solo al iniciar el paneo: capturar en pointerdown redirige el click al <svg> y rompe los marcadores.
        try { svgRef.current.setPointerCapture?.(e.pointerId); } catch (_) {}
      }
      drag.current.lastX = e.clientX; drag.current.lastY = e.clientY;
      const r = svgRef.current.getBoundingClientRect();
      setVb((v) => {
        let nx = clamp(v.x - dx * v.w / r.width, -0.15 * W, W - v.w + 0.15 * W);
        let ny = clamp(v.y - dy * v.h / r.height, -0.15 * H, H - v.h + 0.15 * H);
        return { ...v, x: nx, y: ny };
      });
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) drag.current.pinchDist = 0;
    if (pointers.current.size === 0) drag.current.active = false;
  };
  const guardPan = () => { if (drag.current.panned) { drag.current.panned = false; return true; } return false; };

  if (!ready) return <div className="h-[420px] animate-pulse rounded-3xl bg-[#ECEAE3]" data-testid={`${testid}-loading`} />;

  const sel = selected && markers.find((m) => m.name === selected);
  const leftPct = sel ? clamp(((sel.cx - vb.x) / vb.w) * 100, 4, 96) : 0;
  const topPct = sel ? clamp(((sel.cy - vb.y) / vb.h) * 100, 2, 98) : 0;
  const anchor = leftPct < 30 ? "translate-x-0" : leftPct > 70 ? "-translate-x-full" : "-translate-x-1/2";
  const vAnchor = topPct > 50 ? "-translate-y-full -mt-4" : "mt-4";
  const mscale = vb.w / W;
  const destLabel = sel ? (lang === "es"
    ? (sel.info.count === 1 ? "destino" : "destinos")
    : (sel.info.count === 1 ? "destination" : "destinations")) : "";
  const routeRows = (sel?.info?.routes || []).map((rt) => (
    <li key={rt.slug}>
      <Link to={`/${rt.slug}`} data-testid={`map-route-${rt.slug}`} onClick={() => setSelected(null)}
        className="flex items-center gap-3 rounded-xl p-2 hover:bg-[#F2F6F3] transition-colors duration-200">
        {rt.hero_image
          ? <img src={rt.hero_image} alt={(lang === "en" && rt.name_en) || rt.name} loading="lazy"
              className="h-14 w-14 rounded-lg object-cover shrink-0" />
          : <div className="h-14 w-14 rounded-lg bg-[#E2DFD7] shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-[#16221D] leading-snug">{(lang === "en" && rt.name_en) || rt.name}</span>
          <span className="block text-[11px] text-[#8A968E] mt-0.5">
            {diffLabel(rt.difficulty)}{rt.price ? ` · ${lang === "es" ? "desde" : "from"} $${Number(rt.price).toLocaleString("es-MX")}` : ""}
          </span>
        </span>
      </Link>
    </li>
  ));

  const Pill = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid}
      className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors duration-300 ${active ? "bg-[#6E7E56] text-white border-[#6E7E56]" : "bg-white text-[#5E6D64] border-[#E2DFD7] hover:border-[#6E7E56]"}`}>
      {children}
    </button>
  );

  return (
    <div className="w-full">
      {showFilter && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="map-filter-pills">
          <Pill active={!pillFocus} onClick={() => setPillFocus(null)} tid="map-pill-todas">
            {lang === "es" ? "Todas" : "All"}
          </Pill>
          <Pill active={!!pillFocus} onClick={() => setPillFocus(MEGALOPOLIS)} tid="map-pill-cdmx">
            {lang === "es" ? "Cerca de CDMX" : "Near CDMX"}
          </Pill>
        </div>
      )}

      <div className="relative w-full overflow-hidden rounded-2xl bg-[#F3F1EA]" data-testid={testid}>
        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
          <button onClick={() => zoomCenter(0.8)} data-testid="map-zoom-in" aria-label={lang === "es" ? "Acercar" : "Zoom in"}
            className="h-9 w-9 rounded-full bg-white shadow border border-[#E2DFD7] flex items-center justify-center text-[#16221D] hover:bg-[#6E7E56] hover:text-white transition-colors duration-200">
            <Plus className="h-4 w-4" />
          </button>
          <button onClick={() => zoomCenter(1.25)} data-testid="map-zoom-out" aria-label={lang === "es" ? "Alejar" : "Zoom out"}
            className="h-9 w-9 rounded-full bg-white shadow border border-[#E2DFD7] flex items-center justify-center text-[#16221D] hover:bg-[#6E7E56] hover:text-white transition-colors duration-200">
            <Minus className="h-4 w-4" />
          </button>
        </div>

        <svg ref={svgRef} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} className="w-full h-auto select-none"
          style={{ touchAction: "none", cursor: "grab" }} role="img"
          aria-label="Mapa de destinos de Zenda Hiking en México"
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
          onClick={() => { if (!guardPan()) setSelected(null); }}>
          {shapes.map((s) => (
            <path key={s.id} d={s.d}
              data-testid={s.active ? `map-state-${s.id}` : undefined}
              onClick={(e) => { e.stopPropagation(); if (guardPan()) return; if (s.active) setSelected(s.name === selected ? null : s.name); }}
              className={s.active
                ? "cursor-pointer fill-[#6E7E56] stroke-white hover:fill-[#D94E28] transition-colors duration-300"
                : "fill-[#E2DFD7] stroke-white/80 pointer-events-none"}
              strokeWidth={0.8} />
          ))}
          {markers.map((m) => (
            <g key={`mk-${m.id}`}>
              {(Math.abs(m.cx - m.cx0) > 2 || Math.abs(m.cy - m.cy0) > 2) && (
                <line x1={m.cx0} y1={m.cy0} x2={m.cx} y2={m.cy} className="stroke-[#16221D]/40" strokeWidth={1.2 * mscale} />
              )}
              <g className="cursor-pointer" data-testid={`map-marker-${m.id}`}
                onClick={(e) => { e.stopPropagation(); if (guardPan()) return; setSelected(m.name === selected ? null : m.name); }}>
                <circle cx={m.cx} cy={m.cy} r={15 * mscale}
                  className={`stroke-white transition-colors duration-200 ${selected === m.name ? "fill-[#D94E28]" : "fill-[#16221D] hover:fill-[#D94E28]"}`}
                  strokeWidth={2 * mscale} />
                <text x={m.cx} y={m.cy + 5 * mscale} textAnchor="middle" fontSize={14 * mscale}
                  className="fill-white font-bold select-none pointer-events-none">
                  {m.info.count}
                </text>
              </g>
            </g>
          ))}
        </svg>

        {sel && sel.info && isMobile && (
          <>
            <div className="fixed inset-0 z-40 bg-black/40" data-testid="map-sheet-backdrop" onClick={() => setSelected(null)} />
            <div ref={popRef} data-testid={`map-popup-${sel.id}`}
              className="fixed inset-x-0 bottom-0 z-50 flex max-h-[80vh] flex-col rounded-t-3xl bg-white shadow-[0_-10px_40px_rgb(0,0,0,0.25)]"
              onPointerDown={(e) => e.stopPropagation()}>
              <div className="pt-3 flex justify-center"><span className="h-1.5 w-12 rounded-full bg-[#E2DFD7]" /></div>
              <div className="flex items-center justify-between border-b border-[#F0EEE7] px-5 pb-3 pt-2">
                <div>
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#6E7E56]">
                    <MapPin className="h-3.5 w-3.5" />{sel.name}
                  </p>
                  <p className="font-heading text-xl text-[#16221D]">{sel.info.count} {destLabel}</p>
                </div>
                <button onClick={() => setSelected(null)} data-testid="map-popup-close" aria-label={lang === "es" ? "Cerrar" : "Close"}
                  className="h-10 w-10 rounded-full flex items-center justify-center text-[#8A968E] hover:bg-[#F0EEE7] transition-colors duration-200">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ul className="flex-1 overflow-auto px-3 py-3 space-y-1">{routeRows}</ul>
            </div>
          </>
        )}
        {sel && sel.info && !isMobile && (
          <div ref={popRef}
            className={`absolute z-20 w-72 max-w-[80vw] ${anchor} ${vAnchor} rounded-2xl bg-white shadow-[0_18px_50px_rgb(0,0,0,0.2)] border border-[#E2DFD7] p-4`}
            style={{ left: `${leftPct}%`, top: `${topPct}%` }}
            data-testid={`map-popup-${sel.id}`} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} data-testid="map-popup-close" aria-label={lang === "es" ? "Cerrar" : "Close"}
              className="absolute top-2 right-2 h-7 w-7 rounded-full flex items-center justify-center text-[#8A968E] hover:bg-[#F0EEE7] transition-colors duration-200">
              <X className="h-4 w-4" />
            </button>
            <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#6E7E56]">
              <MapPin className="h-3.5 w-3.5" />{sel.name}
            </p>
            <p className="mt-0.5 font-heading text-lg text-[#16221D]">{sel.info.count} {destLabel}</p>
            <ul className="mt-2 space-y-1 max-h-72 overflow-auto pr-1">{routeRows}</ul>
          </div>
        )}
        )}
      </div>
    </div>
  );
};
